import { useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { AnimatePresence, motion } from 'motion/react'
import { useAuth } from '@/lib/firebase/auth'
import { useProfile } from '@/hooks/queries/useProfile'
import { Sidebar } from './Sidebar'
import { MobileNav } from './MobileNav'
import { PushHandler } from './PushHandler'
import LoginPage from '@/features/auth/LoginPage'
import { Skeleton } from '@/components/ui/skeleton'

export function AppShell() {
  const { status } = useAuth()

  if (status === 'loading') {
    return (
      <div className="flex h-full">
        <div className="hidden w-60 shrink-0 border-r bg-sidebar p-4 md:block">
          <Skeleton className="h-8 w-28" />
          <div className="mt-8 space-y-3">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        </div>
        <div className="flex-1 p-8">
          <Skeleton className="h-8 w-56" />
          <div className="mt-6 space-y-3">
            <Skeleton className="h-24 w-full max-w-3xl" />
            <Skeleton className="h-24 w-full max-w-3xl" />
          </div>
        </div>
      </div>
    )
  }

  if (status === 'signedOut') {
    // Render the login in place so deep links survive sign-in.
    return <LoginPage />
  }

  return <SignedInShell />
}

function SignedInShell() {
  const location = useLocation()

  return (
    <div className="flex h-full flex-col md:flex-row">
      <Sidebar />
      <LiveRedirect />
      <PushHandler />
      <main className="min-h-0 flex-1 overflow-y-auto">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="h-full"
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>
      <MobileNav />
    </div>
  )
}

/**
 * When the profile reports an in-progress call, navigate to its live view once
 * per session — unless the user is already heading somewhere call-related.
 */
function LiveRedirect() {
  const { data } = useProfile()
  const navigate = useNavigate()
  const location = useLocation()
  const redirected = useRef(false)

  useEffect(() => {
    const live = data?.live
    if (!live || redirected.current) return
    redirected.current = true
    if (!location.pathname.startsWith('/live/') && !location.pathname.startsWith('/call/')) {
      void navigate(`/live/${live}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.live])

  return null
}
